import { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Plus, Trash2, Box, Sparkles, ArrowUp, ArrowDown, Copy, Eye, EyeOff } from 'lucide-react';
import api from '../lib/api';

/* ============================= costanti ============================= */
const FACES = ['esterno', 'interno'];
const FACE_LABEL = { esterno: 'Esterno', interno: 'Interno' };
const UNASSIGNED_COLOR = '#cbd5e1';
const BUCKET_LABEL = { non_iniziato: 'Non iniziato', in_corso: 'In corso', completo: 'Completo' };
const BUCKET_HEX = { non_iniziato: '#94a3b8', in_corso: '#d97706', completo: '#16a34a' };
const BUCKET_BADGE = {
  non_iniziato: 'bg-slate-100 text-slate-600 border-slate-300',
  in_corso: 'bg-amber-100 text-amber-700 border-amber-300',
  completo: 'bg-green-100 text-green-700 border-green-300',
};
const PHASE_COLOR_PALETTE = ['#94a3b8', '#f59e0b', '#3b82f6', '#16a34a', '#ef4444', '#a855f7', '#0891b2', '#eab308', '#ec4899', '#64748b'];

const HATCH_STAGE_ORDER = ['da_sabbiare', 'sabbiata_attesa_taglio', 'tagliata_aperta', 'richiusa_riverniciata'];
const HATCH_STAGE_LABEL = {
  da_sabbiare: 'Da sabbiare',
  sabbiata_attesa_taglio: 'Sabbiata (attesa taglio)',
  tagliata_aperta: 'Tagliata (aperta)',
  richiusa_riverniciata: 'Richiusa e riverniciata',
};
const HATCH_STAGE_HEX = {
  da_sabbiare: '#94a3b8',
  sabbiata_attesa_taglio: '#f59e0b',
  tagliata_aperta: '#ef4444',
  richiusa_riverniciata: '#16a34a',
};

/* ============================= dati di default ============================= */
function defaultCycleTemplate() {
  return {
    id: 'standard',
    name: 'Standard (sabbiatura → primer → intermedio → finitura)',
    phases: [
      { id: 'p1', name: 'Sabbiatura', color: '#94a3b8', coats: 1 },
      { id: 'p2', name: 'Sabbiatura e Primer', color: '#f59e0b', coats: 1 },
      { id: 'p3', name: 'Applicazione intermedio', color: '#3b82f6', coats: 1 },
      { id: 'p4', name: 'Finitura', color: '#16a34a', coats: 1 },
    ],
  };
}
function emptyProgress() { return { cycle_id: null, phase_idx: 0, coats_done: 0 }; }
function emptyFaces() { return { esterno: emptyProgress(), interno: emptyProgress() }; }
function defaultFaces(cycleId) {
  const p = { cycle_id: cycleId || null, phase_idx: 0, coats_done: 0 };
  return { esterno: { ...p }, interno: { ...p } };
}
function withPhase(cycleId, phaseIdx, coatsDone) {
  const p = { cycle_id: cycleId, phase_idx: phaseIdx, coats_done: coatsDone };
  return { esterno: { ...p }, interno: { ...p } };
}

function exampleTank() {
  const cycles = [defaultCycleTemplate()];
  const cid = cycles[0].id;
  return {
    name: 'Serbatoio (esempio)',
    cycles,
    shells: [
      { id: 'V1', diameter: 10, height: 2.4, thickness: 12, faces: withPhase(cid, 3, 1) },
      { id: 'V2', diameter: 10, height: 2.4, thickness: 10, faces: withPhase(cid, 2, 1) },
      { id: 'V3', diameter: 10, height: 2.4, thickness: 8, faces: withPhase(cid, 1, 1) },
    ],
    bottom: { type: 'dished', plates: generateBottomPlates(10, 2, 3, cid) },
    trincarino: { plates: generateTrincarinoPlates(16, cid) },
    roof: { kind: 'fisso', type: 'cone', faces: withPhase(cid, 0, 0), plates: [], pontoon: { faces: emptyFaces() } },
    nozzles: [
      { id: 'B1', shellId: 'V1', label: 'N1 — Ingresso uomo', elevation: 1.2, angle: 0, diameter: 600, length: 300, faces: withPhase(cid, 3, 1) },
      { id: 'B2', shellId: 'V3', label: 'N2 — Sfiato', elevation: 1.0, angle: 90, diameter: 100, length: 200, faces: withPhase(cid, 0, 0) },
    ],
    stiffeners: [
      { id: 'C1', shellId: 'V3', label: 'Cordolo di rinforzo', elevation: 2.2, faces: withPhase(cid, 1, 1) },
    ],
    hatches: [
      { id: 'P1', shellId: 'V2', label: 'Portina di accesso', elevation: 1.2, angle: 45, width: 600, height: 800, stage: 'sabbiata_attesa_taglio' },
    ],
  };
}
function emptyTank() {
  const cycles = [defaultCycleTemplate()];
  return {
    name: 'Nuovo serbatoio',
    cycles,
    shells: [],
    bottom: { type: 'flat', plates: [] },
    trincarino: { plates: [] },
    roof: { kind: 'fisso', type: 'cone', faces: emptyFaces(), plates: [], pontoon: { faces: emptyFaces() } },
    nozzles: [],
    stiffeners: [],
    hatches: [],
  };
}
function cloneTank(t) { return JSON.parse(JSON.stringify(t)); }

/* ============================= generatori lamiere ============================= */
function generateBottomPlates(diameter, plateWidth, plateLength, defaultCycleId) {
  const R = Math.max(0.5, (diameter || 1) / 2);
  const pw = Math.max(0.3, plateWidth || 2);
  const pl = Math.max(0.3, plateLength || 8);
  const plates = [];
  let row = 0;
  for (let z = -R + pw / 2; z < R; z += pw) {
    row++;
    const halfChord = Math.sqrt(Math.max(0, R * R - z * z));
    const chord = halfChord * 2;
    if (chord < 0.2) continue;
    const n = Math.max(1, Math.round(chord / pl));
    const segLen = chord / n;
    for (let i = 0; i < n; i++) {
      const x = -halfChord + segLen * (i + 0.5);
      plates.push({ id: `F${row}-${i + 1}`, row, seq: i + 1, cx: x, cz: z, width: segLen * 0.96, length: pw * 0.92, faces: defaultFaces(defaultCycleId) });
    }
  }
  return plates;
}
function generateTrincarinoPlates(count, defaultCycleId) {
  const n = Math.max(4, Math.min(200, Math.round(count || 38)));
  const step = 360 / n;
  const plates = [];
  for (let i = 0; i < n; i++) plates.push({ id: `T${i + 1}`, seq: i + 1, angle_from: i * step, angle_to: (i + 1) * step, faces: defaultFaces(defaultCycleId) });
  return plates;
}
function generateRoofPlates(gores, defaultCycleId) {
  const n = Math.max(4, Math.min(120, Math.round(gores || 24)));
  const step = 360 / n;
  const plates = [];
  for (let i = 0; i < n; i++) plates.push({ id: `R1-${i + 1}`, ring: 1, seq: i + 1, angle_from: i * step, angle_to: (i + 1) * step, faces: defaultFaces(defaultCycleId) });
  return plates;
}

/* ============================= helper cicli / colori ============================= */
function cycleById(tank, id) { return (tank.cycles || []).find((c) => c.id === id) || null; }
function phaseInfo(tank, progress) {
  const prog = progress || emptyProgress();
  const cycle = prog.cycle_id ? cycleById(tank, prog.cycle_id) : null;
  if (!cycle || !cycle.phases.length) return { assigned: false, color: UNASSIGNED_COLOR, label: 'Non assegnato', cycle: null, phase: null, complete: false };
  const idx = Math.max(0, Math.min(cycle.phases.length - 1, prog.phase_idx || 0));
  const phase = cycle.phases[idx];
  const complete = idx === cycle.phases.length - 1 && (prog.coats_done || 0) >= phase.coats;
  const label = phase.coats > 1 ? `${phase.name} (${prog.coats_done || 0}/${phase.coats} mani)` : phase.name;
  return { assigned: true, color: phase.color, label, cycle, phase, phaseIdx: idx, complete };
}
function bucketOf(tank, progress) {
  const info = phaseInfo(tank, progress);
  if (!info.assigned) return 'non_iniziato';
  if (info.complete) return 'completo';
  if ((progress.phase_idx || 0) === 0 && (progress.coats_done || 0) === 0) return 'non_iniziato';
  return 'in_corso';
}
function componentColor(tank, kind, obj, faceView) {
  if (kind === 'portina') return HATCH_STAGE_HEX[obj.stage] || HATCH_STAGE_HEX.da_sabbiare;
  const progress = (obj.faces || {})[faceView] || emptyProgress();
  return phaseInfo(tank, progress).color;
}

/* ============================= alberatura componenti ============================= */
function allComponents(tank) {
  const list = [];
  (tank.shells || []).forEach((s) => list.push({ kind: 'virola', obj: s }));
  ((tank.bottom && tank.bottom.plates) || []).forEach((p) => list.push({ kind: 'fondo_lamiera', obj: p }));
  ((tank.trincarino && tank.trincarino.plates) || []).forEach((p) => list.push({ kind: 'trincarino_lamiera', obj: p }));
  if (tank.roof) {
    if (tank.roof.kind === 'galleggiante') {
      (tank.roof.plates || []).forEach((p) => list.push({ kind: 'tetto_lamiera', obj: p }));
      tank.roof.pontoon = tank.roof.pontoon || { faces: emptyFaces() };
      tank.roof.pontoon.id = tank.roof.pontoon.id || 'PONTONE';
      list.push({ kind: 'pontone', obj: tank.roof.pontoon });
    } else {
      tank.roof.id = tank.roof.id || 'TETTO';
      list.push({ kind: 'tetto', obj: tank.roof });
    }
  }
  (tank.nozzles || []).forEach((n) => list.push({ kind: 'bocchello', obj: n }));
  (tank.stiffeners || []).forEach((c) => list.push({ kind: 'cordolo', obj: c }));
  (tank.hatches || []).forEach((h) => list.push({ kind: 'portina', obj: h }));
  return list;
}
function findComponentIn(tank, id) { return allComponents(tank).find((c) => c.obj.id === id) || null; }
function displayName(kind, obj) {
  if (kind === 'virola') return 'Virola ' + String(obj.id).replace('V', '');
  if (kind === 'fondo_lamiera') return `Fondo — corso ${obj.row}, lamiera ${obj.seq}`;
  if (kind === 'trincarino_lamiera') return `Trincarino ${obj.seq}`;
  if (kind === 'tetto') return 'Tetto';
  if (kind === 'tetto_lamiera') return `Tetto — spicchio ${obj.seq}`;
  if (kind === 'pontone') return 'Pontone';
  return obj.label || obj.id;
}
function dims(kind, obj) {
  if (kind === 'virola') return [['Diametro', obj.diameter.toFixed(2) + ' m'], ['Altezza', obj.height.toFixed(2) + ' m'], ['Spessore', obj.thickness + ' mm']];
  if (kind === 'fondo_lamiera') return [['Corso', String(obj.row)], ['Lamiera', String(obj.seq)], ['Dimensioni', obj.width.toFixed(2) + ' × ' + obj.length.toFixed(2) + ' m']];
  if (kind === 'trincarino_lamiera') return [['Settore', obj.angle_from.toFixed(1) + '° – ' + obj.angle_to.toFixed(1) + '°']];
  if (kind === 'tetto_lamiera') return [['Settore', obj.angle_from.toFixed(1) + '° – ' + obj.angle_to.toFixed(1) + '°']];
  if (kind === 'bocchello') return [['Virola', obj.shellId], ['Quota', obj.elevation.toFixed(2) + ' m'], ['Angolo', obj.angle + '°'], ['DN', obj.diameter + ' mm']];
  if (kind === 'cordolo') return [['Virola', obj.shellId], ['Quota', obj.elevation.toFixed(2) + ' m']];
  if (kind === 'portina') return [['Virola', obj.shellId], ['Quota', obj.elevation.toFixed(2) + ' m'], ['Angolo', obj.angle + '°'], ['Dimensioni', obj.width + ' × ' + obj.height + ' mm']];
  return [];
}
function nextId(prefix, arr) {
  let n = arr.length + 1;
  const used = new Set(arr.map((x) => x.id));
  while (used.has(prefix + n)) n++;
  return prefix + n;
}

/* ============================= componente principale ============================= */
export default function ProjectTankPanel({ projectId }) {
  const [tank, setTank] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [faceView, setFaceView] = useState('esterno');
  const [viewMode, setViewMode] = useState('3d'); // '3d' | 'tetto'
  const [view, setView] = useState('componenti'); // 'componenti' | 'editor'
  const [savingSample, setSavingSample] = useState(false);

  const hostRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const raycasterRef = useRef(null);
  const pickablesRef = useRef([]);
  const meshByIdRef = useRef({});
  const gridRef = useRef(null);
  const camRef = useRef({ theta: Math.PI * 0.28, phi: Math.PI * 0.36, radius: 22, target: new THREE.Vector3(), sizedOnce: false });

  /* ---------------- caricamento / persistenza ---------------- */
  const load = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    try {
      const res = await api.get(`/project-tanks/${projectId}`);
      setTank(res.data);
      setDraft(cloneTank(res.data));
    } catch (e) {
      if (e.response?.status === 404) { setTank(null); setNotFound(true); }
      else console.error(e);
    } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const persist = useCallback(async (nextTank) => {
    try {
      const res = await api.put(`/project-tanks/${projectId}`, nextTank);
      return res.data;
    } catch (e) {
      console.error(e);
      alert(e.response?.data?.detail || 'Errore salvataggio serbatoio');
      return null;
    }
  }, [projectId]);

  const initTank = async (data) => {
    setSavingSample(true);
    const saved = await persist(data);
    setSavingSample(false);
    if (saved) { setTank(saved); setDraft(cloneTank(saved)); setNotFound(false); }
  };

  /* ---------------- three.js: init una sola volta ---------------- */
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(42, Math.max(1, host.clientWidth) / Math.max(1, host.clientHeight), 0.1, 500);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    renderer.setSize(host.clientWidth, host.clientHeight);
    host.appendChild(renderer.domElement);

    scene.add(new THREE.HemisphereLight(0xffffff, 0xcbd5e1, 1.0));
    const d1 = new THREE.DirectionalLight(0xffffff, 0.9); d1.position.set(8, 14, 10); scene.add(d1);
    const d2 = new THREE.DirectionalLight(0xffffff, 0.35); d2.position.set(-10, 6, -8); scene.add(d2);

    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    raycasterRef.current = new THREE.Raycaster();

    const updateCameraPos = () => {
      const c = camRef.current;
      c.phi = Math.max(0.06, Math.min(Math.PI - 0.12, c.phi));
      const x = c.target.x + c.radius * Math.sin(c.phi) * Math.sin(c.theta);
      const y = c.target.y + c.radius * Math.cos(c.phi);
      const z = c.target.z + c.radius * Math.sin(c.phi) * Math.cos(c.theta);
      camera.position.set(x, y, z);
      camera.lookAt(c.target);
    };
    updateCameraPos();

    let dragging = false, lastX = 0, lastY = 0, moved = 0;
    const el = renderer.domElement;
    el.style.cursor = 'grab';
    const onDown = (e) => { dragging = true; moved = 0; lastX = e.clientX; lastY = e.clientY; el.setPointerCapture(e.pointerId); el.style.cursor = 'grabbing'; };
    const onMove = (e) => {
      if (!dragging) return;
      const dx = e.clientX - lastX, dy = e.clientY - lastY;
      moved += Math.abs(dx) + Math.abs(dy);
      camRef.current.theta -= dx * 0.007;
      camRef.current.phi -= dy * 0.007;
      lastX = e.clientX; lastY = e.clientY;
      updateCameraPos();
    };
    const pointer = new THREE.Vector2();
    const onUp = (e) => {
      dragging = false; el.style.cursor = 'grab';
      if (moved >= 5) return;
      const rect = el.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycasterRef.current.setFromCamera(pointer, camera);
      const hits = raycasterRef.current.intersectObjects(pickablesRef.current, false);
      if (!hits.length) { setSelectedIds(new Set()); setActiveId(null); return; }
      const id = hits[0].object.userData.id;
      setActiveId(id);
      setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
    };
    const onWheel = (e) => {
      e.preventDefault();
      camRef.current.radius = Math.max(3, Math.min(160, camRef.current.radius * (1 + Math.sign(e.deltaY) * 0.08)));
      updateCameraPos();
    };
    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('wheel', onWheel, { passive: false });

    let raf;
    const animate = () => { raf = requestAnimationFrame(animate); renderer.render(scene, camera); };
    animate();

    const ro = new ResizeObserver(() => {
      const w = host.clientWidth, h = host.clientHeight;
      if (w < 2 || h < 2) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(host);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('wheel', onWheel);
      renderer.dispose();
      if (host.contains(el)) host.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ---------------- ricostruzione geometrie ---------------- */
  const buildScene = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene || !tank) return;

    pickablesRef.current.forEach((m) => { scene.remove(m); m.geometry.dispose(); m.material.dispose(); });
    pickablesRef.current = [];
    meshByIdRef.current = {};
    if (gridRef.current) { scene.remove(gridRef.current); gridRef.current.geometry.dispose(); gridRef.current.material.dispose(); gridRef.current = null; }

    const addPickable = (mesh, id, kind) => {
      mesh.userData = { id, kind };
      scene.add(mesh);
      pickablesRef.current.push(mesh);
      meshByIdRef.current[id] = mesh;
    };
    const material = (hex, doubleSide) => new THREE.MeshStandardMaterial({ color: new THREE.Color(hex), metalness: 0.2, roughness: 0.6, side: doubleSide ? THREE.DoubleSide : THREE.FrontSide });
    const colorOf = (kind, obj) => componentColor(tank, kind, obj, faceView);

    const roofOnly = viewMode === 'tetto';

    const shells = tank.shells || [];
    let y = 0;
    const shellGeo = {};
    shells.forEach((s) => {
      const r = Math.max(0.05, s.diameter / 2);
      const h = Math.max(0.05, s.height);
      shellGeo[s.id] = { baseY: y, topY: y + h, radius: r };
      if (!roofOnly) {
        const geo = new THREE.CylinderGeometry(r, r, h, 56, 1, true);
        const mesh = new THREE.Mesh(geo, material(colorOf('virola', s), true));
        mesh.position.set(0, y + h / 2, 0);
        addPickable(mesh, s.id, 'virola');
      }
      y += h;
    });
    const totalHeight = y;
    const baseRadius = shells.length ? Math.max(0.05, shells[0].diameter / 2) : 4;
    const topRadius = shells.length ? Math.max(0.05, shells[shells.length - 1].diameter / 2) : 4;

    if (!roofOnly && tank.bottom) {
      const plates = tank.bottom.plates || [];
      if (plates.length) {
        plates.forEach((p) => {
          const w = Math.max(0.05, p.width), l = Math.max(0.05, p.length);
          const geo = new THREE.BoxGeometry(w, 0.05, l);
          const mesh = new THREE.Mesh(geo, material(colorOf('fondo_lamiera', p)));
          mesh.position.set(p.cx, -0.03, p.cz);
          addPickable(mesh, p.id, 'fondo_lamiera');
        });
      } else if (baseRadius) {
        // placeholder pieno finche' non si generano le lamiere
        const geo = tank.bottom.type === 'dished'
          ? new THREE.SphereGeometry(baseRadius, 40, 16, 0, Math.PI * 2, Math.PI * 0.62, Math.PI * 0.38)
          : new THREE.CylinderGeometry(baseRadius, baseRadius, baseRadius * 0.05, 56);
        const mesh = new THREE.Mesh(geo, new THREE.MeshStandardMaterial({ color: '#e2e8f0', metalness: 0.1, roughness: 0.8, transparent: true, opacity: 0.55 }));
        mesh.position.set(0, tank.bottom.type === 'dished' ? 0 : -baseRadius * 0.025, 0);
        scene.add(mesh);
      }
    }

    if (!roofOnly && baseRadius) {
      const trincPlates = (tank.trincarino && tank.trincarino.plates) || [];
      const trincW = Math.min(1.1, baseRadius * 0.3);
      trincPlates.forEach((p) => {
        const geo = new THREE.RingGeometry(Math.max(0.1, baseRadius - trincW), baseRadius, 6, 1, p.angle_from * Math.PI / 180, (p.angle_to - p.angle_from) * Math.PI / 180);
        geo.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(geo, material(colorOf('trincarino_lamiera', p), true));
        mesh.position.set(0, 0.01, 0);
        addPickable(mesh, p.id, 'trincarino_lamiera');
      });
    }

    if (tank.roof) {
      if (tank.roof.kind === 'galleggiante') {
        const plates = tank.roof.plates || [];
        const deckR = Math.max(0.2, topRadius * 0.97);
        plates.forEach((p) => {
          const geo = new THREE.RingGeometry(0.05, deckR, 4, 1, p.angle_from * Math.PI / 180, (p.angle_to - p.angle_from) * Math.PI / 180);
          geo.rotateX(-Math.PI / 2);
          const mesh = new THREE.Mesh(geo, material(colorOf('tetto_lamiera', p), true));
          mesh.position.set(0, totalHeight, 0);
          addPickable(mesh, p.id, 'tetto_lamiera');
        });
        const tube = Math.max(0.05, topRadius * 0.04);
        const pGeo = new THREE.TorusGeometry(Math.max(0.2, deckR - tube), tube, 10, 48);
        const pMesh = new THREE.Mesh(pGeo, material(colorOf('pontone', tank.roof.pontoon)));
        pMesh.rotation.x = Math.PI / 2;
        pMesh.position.set(0, totalHeight, 0);
        addPickable(pMesh, tank.roof.pontoon.id || 'PONTONE', 'pontone');
      } else {
        let rMesh;
        if (tank.roof.type === 'dome') {
          const geo = new THREE.SphereGeometry(topRadius, 40, 16, 0, Math.PI * 2, 0, Math.PI * 0.38);
          rMesh = new THREE.Mesh(geo, material(colorOf('tetto', tank.roof)));
          rMesh.position.set(0, totalHeight, 0);
        } else if (tank.roof.type === 'flat') {
          const geo = new THREE.CylinderGeometry(topRadius, topRadius, topRadius * 0.05, 56);
          rMesh = new THREE.Mesh(geo, material(colorOf('tetto', tank.roof)));
          rMesh.position.set(0, totalHeight + topRadius * 0.025, 0);
        } else {
          const coneH = topRadius * 0.55;
          const geo = new THREE.ConeGeometry(topRadius, coneH, 56);
          rMesh = new THREE.Mesh(geo, material(colorOf('tetto', tank.roof)));
          rMesh.position.set(0, totalHeight + coneH / 2, 0);
        }
        if (!(faceView === 'interno' && !roofOnly)) addPickable(rMesh, tank.roof.id || 'TETTO', 'tetto');
      }
    }

    if (!roofOnly) {
      (tank.nozzles || []).forEach((n) => {
        const sg = shellGeo[n.shellId];
        if (!sg) return;
        const r = Math.max(0.01, n.diameter / 1000 / 2);
        const len = Math.max(0.15, n.length / 1000);
        const geo = new THREE.CylinderGeometry(r, r, len, 20);
        const mesh = new THREE.Mesh(geo, material(colorOf('bocchello', n)));
        const ang = (n.angle || 0) * Math.PI / 180;
        const dir = new THREE.Vector3(Math.cos(ang), 0, Math.sin(ang));
        const elevY = sg.baseY + Math.min(Math.max(n.elevation || 0, 0), sg.topY - sg.baseY);
        const outR = sg.radius + len / 2;
        mesh.position.set(dir.x * outR, elevY, dir.z * outR);
        mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), dir);
        addPickable(mesh, n.id, 'bocchello');
      });

      (tank.stiffeners || []).forEach((c) => {
        const sg = shellGeo[c.shellId];
        if (!sg) return;
        const tube = Math.max(0.04, sg.radius * 0.012);
        const geo = new THREE.TorusGeometry(sg.radius + tube, tube, 10, 64);
        const mesh = new THREE.Mesh(geo, material(colorOf('cordolo', c)));
        mesh.rotation.x = Math.PI / 2;
        const elevY = sg.baseY + Math.min(Math.max(c.elevation || 0, 0), sg.topY - sg.baseY);
        mesh.position.set(0, elevY, 0);
        addPickable(mesh, c.id, 'cordolo');
      });

      (tank.hatches || []).forEach((hh) => {
        const sg = shellGeo[hh.shellId];
        if (!sg) return;
        const w = Math.max(0.05, (hh.width || 300) / 1000);
        const hgt = Math.max(0.05, (hh.height || 300) / 1000);
        const geo = new THREE.PlaneGeometry(w, hgt);
        const mesh = new THREE.Mesh(geo, material(colorOf('portina', hh), true));
        const ang = (hh.angle || 0) * Math.PI / 180;
        const dir = new THREE.Vector3(Math.cos(ang), 0, Math.sin(ang));
        const elevY = sg.baseY + Math.min(Math.max(hh.elevation || 0, 0), sg.topY - sg.baseY);
        const outR = sg.radius + 0.02;
        mesh.position.set(dir.x * outR, elevY, dir.z * outR);
        mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir);
        addPickable(mesh, hh.id, 'portina');
      });
    }

    const grid = new THREE.GridHelper(Math.max(baseRadius, topRadius, 4) * 3.2, 16, '#cbd5e1', '#cbd5e1');
    grid.material.opacity = 0.4; grid.material.transparent = true;
    grid.position.y = -0.01;
    scene.add(grid);
    gridRef.current = grid;

    if (roofOnly) {
      camRef.current.target = new THREE.Vector3(0, totalHeight, 0);
      camRef.current.theta = 0;
      camRef.current.phi = 0.08;
      camRef.current.radius = Math.max(4, topRadius * 2.3);
    } else {
      camRef.current.target = new THREE.Vector3(0, totalHeight / 2, 0);
      if (!camRef.current.sizedOnce) {
        camRef.current.radius = Math.max(6, (baseRadius + totalHeight) * 1.15);
        camRef.current.sizedOnce = true;
      }
    }
    const c = camRef.current;
    c.phi = Math.max(0.06, Math.min(Math.PI - 0.12, c.phi));
    const camera = cameraRef.current;
    if (camera) {
      const x = c.target.x + c.radius * Math.sin(c.phi) * Math.sin(c.theta);
      const yy = c.target.y + c.radius * Math.cos(c.phi);
      const z = c.target.z + c.radius * Math.sin(c.phi) * Math.cos(c.theta);
      camera.position.set(x, yy, z);
      camera.lookAt(c.target);
    }

    applyHighlight();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tank, faceView, viewMode]);

  useEffect(() => { buildScene(); }, [buildScene]);

  const applyHighlight = useCallback(() => {
    Object.keys(meshByIdRef.current).forEach((id) => {
      const m = meshByIdRef.current[id];
      if (id === activeId) { m.material.emissive.set('#2563eb'); m.material.emissiveIntensity = 0.55; }
      else if (selectedIds.has(id)) { m.material.emissive.set('#06b6d4'); m.material.emissiveIntensity = 0.35; }
      else { m.material.emissive.set('#000000'); m.material.emissiveIntensity = 0; }
    });
  }, [activeId, selectedIds]);
  useEffect(() => { applyHighlight(); }, [applyHighlight]);

  /* ---------------- selezione ---------------- */
  const pickSingle = useCallback((id) => { setActiveId(id); setSelectedIds(new Set([id])); }, []);
  const toggleSelect = useCallback((id) => {
    setActiveId(id);
    setSelectedIds((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  }, []);
  const clearSelection = useCallback(() => { setActiveId(null); setSelectedIds(new Set()); }, []);

  /* ---------------- azioni avanzamento ---------------- */
  const updateFaces = useCallback((ids, patchFn) => {
    setTank((prev) => {
      if (!prev) return prev;
      const next = cloneTank(prev);
      ids.forEach((id) => {
        const comp = findComponentIn(next, id);
        if (!comp || comp.kind === 'portina') return;
        comp.obj.faces = comp.obj.faces || emptyFaces();
        const cur = comp.obj.faces[faceView] || emptyProgress();
        comp.obj.faces[faceView] = patchFn(cur, next);
      });
      persist(next);
      setDraft(cloneTank(next));
      return next;
    });
  }, [persist, faceView]);

  const setPhase = useCallback((ids, cycleId, phaseIdx) => {
    updateFaces(ids, () => ({ cycle_id: cycleId, phase_idx: phaseIdx, coats_done: 0 }));
  }, [updateFaces]);

  const bumpCoats = useCallback((ids, delta) => {
    updateFaces(ids, (cur, t) => {
      const cycle = cycleById(t, cur.cycle_id);
      const phase = cycle ? cycle.phases[cur.phase_idx] : null;
      const max = phase ? phase.coats : 1;
      return { ...cur, coats_done: Math.max(0, Math.min(max, (cur.coats_done || 0) + delta)) };
    });
  }, [updateFaces]);

  const setHatchStage = useCallback((id, stage) => {
    setTank((prev) => {
      if (!prev) return prev;
      const next = cloneTank(prev);
      const h = (next.hatches || []).find((x) => x.id === id);
      if (!h) return prev;
      h.stage = stage;
      persist(next);
      setDraft(cloneTank(next));
      return next;
    });
  }, [persist]);

  const applyDraft = async () => {
    const saved = await persist(draft);
    if (saved) { setTank(saved); setDraft(cloneTank(saved)); }
  };

  const components = tank ? allComponents(tank) : [];
  const counts = tank ? components.filter((c) => c.kind !== 'portina').reduce((acc, c) => {
    const b = bucketOf(tank, (c.obj.faces || {})[faceView]);
    acc[b] = (acc[b] || 0) + 1;
    return acc;
  }, {}) : {};
  const active = tank && activeId ? findComponentIn(tank, activeId) : null;
  const showEmpty = !loading && (notFound || !tank);

  /* ---------------- render ----------------
   * Il div che ospita il canvas (ref={hostRef}) resta SEMPRE montato, anche durante il
   * caricamento o quando non c'e' ancora un serbatoio: l'effetto che inizializza three.js
   * gira una sola volta al mount e altrimenti troverebbe hostRef.current nullo se questo
   * div comparisse solo dopo un return condizionale (bug gia' preso una volta). */
  return (
    <div className="space-y-3" data-testid="project-tank-panel">
      <div className="flex flex-wrap items-center gap-2 min-h-[32px]">
        {tank ? (
          <Input value={tank.name} onChange={(e) => { const n = { ...tank, name: e.target.value }; setTank(n); }} onBlur={() => persist(tank)} className="h-8 max-w-xs font-semibold" data-testid="tank-name-input" />
        ) : (
          <p className="text-sm font-semibold text-slate-700">Serbatoio</p>
        )}
        {tank && (
          <>
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden ml-2">
              {FACES.map((f) => (
                <button key={f} onClick={() => setFaceView(f)}
                  className={`px-2.5 py-1 text-[11px] font-semibold ${faceView === f ? 'bg-blue-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  data-testid={`face-toggle-${f}`}>
                  {f === 'esterno' ? <Eye size={11} className="inline mr-1 -mt-0.5" /> : <EyeOff size={11} className="inline mr-1 -mt-0.5" />}
                  {FACE_LABEL[f]}
                </button>
              ))}
            </div>
            <div className="flex items-center rounded-md border border-slate-200 overflow-hidden">
              {[['3d', 'Vista 3D'], ['tetto', 'Solo tetto']].map(([v, label]) => (
                <button key={v} onClick={() => setViewMode(v)}
                  className={`px-2.5 py-1 text-[11px] font-semibold ${viewMode === v ? 'bg-slate-800 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                  data-testid={`view-toggle-${v}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              {['non_iniziato', 'in_corso', 'completo'].map((b) => (
                <Badge key={b} variant="outline" className={BUCKET_BADGE[b]}>
                  <span className="font-mono font-bold mr-1">{counts[b] || 0}</span>{BUCKET_LABEL[b]}
                </Badge>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-3" style={{ height: 620 }}>
        <div className="relative flex-1 min-w-0 rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
          <div ref={hostRef} className="absolute inset-0" />

          {loading && (
            <div className="absolute inset-0 flex items-center justify-center text-sm text-slate-400">Caricamento serbatoio...</div>
          )}

          {showEmpty && (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="text-center space-y-4 max-w-sm">
                <Box size={32} className="mx-auto text-slate-300" />
                <div>
                  <p className="text-sm font-medium text-slate-700">Nessun serbatoio configurato per questo progetto</p>
                  <p className="text-xs text-slate-400 mt-1">Crea la struttura (virole, fondo, trincarino, tetto, bocchelli, cordoli, portine) per iniziare a tracciare l'avanzamento.</p>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Button variant="outline" onClick={() => initTank(emptyTank())} disabled={savingSample} data-testid="tank-init-empty">Inizia vuoto</Button>
                  <Button onClick={() => initTank(exampleTank())} disabled={savingSample} data-testid="tank-init-example"><Sparkles size={14} className="mr-1" /> Carica esempio</Button>
                </div>
              </div>
            </div>
          )}

          {tank && (
            <>
              <div className="absolute top-2 left-2 text-[11px] text-slate-500 bg-white/90 border border-slate-200 rounded-md px-2 py-1 max-w-[70%]">
                Trascina per ruotare · rotella per zoom · click su un pezzo (di nuovo per deselezionare) · vista <b>{faceView === 'esterno' ? 'esterna' : 'interna'}</b>
                {viewMode === 'tetto' ? ' · solo tetto' : ''}
              </div>
              {selectedIds.size > 0 && (
                <div className="absolute top-2 right-2">
                  <Badge className="bg-cyan-100 text-cyan-700 border-cyan-300">{selectedIds.size} selezionat{selectedIds.size > 1 ? 'i' : 'o'}</Badge>
                </div>
              )}
              <div className="absolute bottom-2 left-2 flex flex-wrap gap-3 text-[11px] bg-white/90 border border-slate-200 rounded-md px-3 py-1.5 max-w-[85%]">
                {['non_iniziato', 'in_corso', 'completo'].map((b) => (
                  <span key={b} className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: BUCKET_HEX[b] }} />{BUCKET_LABEL[b]}</span>
                ))}
                {(tank.hatches || []).length > 0 && HATCH_STAGE_ORDER.map((s) => (
                  <span key={s} className="flex items-center gap-1.5 border-l border-slate-200 pl-3 first:border-0 first:pl-0"><span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: HATCH_STAGE_HEX[s] }} />{HATCH_STAGE_LABEL[s]}</span>
                ))}
              </div>
              {active && (
                <div className="absolute bottom-2 right-2 w-72 bg-white border border-slate-200 rounded-lg shadow-lg p-3" data-testid="tank-inspector">
                  <InspectorBody tank={tank} comp={active} faceView={faceView} onSetPhase={(cid, pidx) => setPhase([active.obj.id], cid, pidx)} onBumpCoats={(d) => bumpCoats([active.obj.id], d)} onHatchStage={(s) => setHatchStage(active.obj.id, s)} onClose={clearSelection} />
                </div>
              )}
            </>
          )}
        </div>

        <div className="w-full lg:w-96 flex-shrink-0 border border-slate-200 rounded-lg bg-white flex flex-col min-h-0">
          {tank ? (
            <>
              <div className="flex border-b border-slate-200 flex-shrink-0">
                <button onClick={() => setView('componenti')} className={`flex-1 text-xs font-semibold py-2.5 border-b-2 ${view === 'componenti' ? 'text-blue-700 border-blue-600' : 'text-slate-400 border-transparent'}`}>Componenti</button>
                <button onClick={() => setView('editor')} className={`flex-1 text-xs font-semibold py-2.5 border-b-2 ${view === 'editor' ? 'text-blue-700 border-blue-600' : 'text-slate-400 border-transparent'}`}>Dati serbatoio</button>
              </div>
              {view === 'componenti' && selectedIds.size > 1 && (
                <BulkBar tank={tank} selectedIds={selectedIds} components={components} faceView={faceView} onSetPhase={(cid, pidx) => setPhase([...selectedIds], cid, pidx)} onBumpCoats={(d) => bumpCoats([...selectedIds], d)} onClear={clearSelection} />
              )}
              <div className="flex-1 overflow-y-auto p-3">
                {view === 'componenti' ? (
                  <ComponentList tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={pickSingle} onToggle={toggleSelect} onHatchStage={setHatchStage} />
                ) : (
                  <EditorForm draft={draft} setDraft={setDraft} onApply={applyDraft} onLoadExample={() => setDraft(exampleTank())} />
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-xs text-slate-400 p-4 text-center">
              {loading ? 'Caricamento...' : 'Configura il serbatoio per iniziare'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================= inspector ============================= */
function InspectorBody({ tank, comp, faceView, onSetPhase, onBumpCoats, onHatchStage, onClose }) {
  const { kind, obj } = comp;
  if (kind === 'portina') {
    return (
      <div>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400">portina di accesso</p>
            <p className="text-sm font-semibold text-slate-800">{displayName(kind, obj)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-base leading-none">&times;</button>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 mb-3">
          {dims(kind, obj).map(([k, v]) => <span key={k} className="text-[11px] text-slate-500">{k}: <span className="font-mono font-medium text-slate-700">{v}</span></span>)}
        </div>
        <div className="space-y-1">
          {HATCH_STAGE_ORDER.map((s) => (
            <button key={s} onClick={() => onHatchStage(s)}
              className={`w-full text-left text-[11px] font-medium rounded-md py-1.5 px-2 border flex items-center gap-2 ${obj.stage === s ? 'font-bold' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
              style={obj.stage === s ? { background: HATCH_STAGE_HEX[s] + '22', borderColor: HATCH_STAGE_HEX[s], color: HATCH_STAGE_HEX[s] } : undefined}>
              <span className="w-2 h-2 rounded-full inline-block" style={{ background: HATCH_STAGE_HEX[s] }} />{HATCH_STAGE_LABEL[s]}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const cycles = tank.cycles || [];
  const progress = (obj.faces || {})[faceView] || emptyProgress();
  const otherFace = faceView === 'esterno' ? 'interno' : 'esterno';
  const otherProgress = (obj.faces || {})[otherFace] || emptyProgress();
  const info = phaseInfo(tank, progress);
  const otherInfo = phaseInfo(tank, otherProgress);

  return (
    <div>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400">{kind.replace('_', ' ')} · {FACE_LABEL[faceView]}</p>
          <p className="text-sm font-semibold text-slate-800">{displayName(kind, obj)}</p>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 text-base leading-none">&times;</button>
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 mb-2">
        {dims(kind, obj).map(([k, v]) => <span key={k} className="text-[11px] text-slate-500">{k}: <span className="font-mono font-medium text-slate-700">{v}</span></span>)}
      </div>

      <div className="mb-2">
        <Label className="text-[9px] text-slate-400">Ciclo ({FACE_LABEL[faceView]})</Label>
        <Select value={progress.cycle_id || '__none'} onValueChange={(v) => onSetPhase(v === '__none' ? null : v, 0)}>
          <SelectTrigger className="h-7 text-[11px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__none">Non assegnato</SelectItem>
            {cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {info.cycle && (
        <>
          <div className="flex flex-col gap-1 mb-2">
            {info.cycle.phases.map((p, idx) => (
              <button key={p.id} onClick={() => onSetPhase(progress.cycle_id, idx)}
                className={`text-left text-[11px] font-medium rounded-md py-1.5 px-2 border flex items-center justify-between ${idx === progress.phase_idx ? 'font-bold' : 'bg-slate-50 text-slate-500 border-slate-200'}`}
                style={idx === progress.phase_idx ? { background: p.color + '22', borderColor: p.color, color: p.color } : undefined}>
                <span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full inline-block" style={{ background: p.color }} />{p.name}</span>
                {p.coats > 1 && idx === progress.phase_idx && <span className="font-mono">{progress.coats_done}/{p.coats}</span>}
              </button>
            ))}
          </div>
          {info.phase && info.phase.coats > 1 && (
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] text-slate-400">Mani:</span>
              <button onClick={() => onBumpCoats(-1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 text-xs">−</button>
              <span className="text-[11px] font-mono font-semibold">{progress.coats_done}/{info.phase.coats}</span>
              <button onClick={() => onBumpCoats(1)} className="w-6 h-6 rounded border border-slate-200 text-slate-500 text-xs">+</button>
            </div>
          )}
        </>
      )}

      <div className="pt-2 mt-1 border-t border-slate-100 text-[11px] text-slate-500">
        {FACE_LABEL[otherFace]}: <span className="font-medium" style={{ color: otherInfo.color }}>{otherInfo.label}</span>
        <span className="text-slate-300"> · cambia vista per modificare</span>
      </div>
    </div>
  );
}

/* ============================= barra azioni bulk ============================= */
function BulkBar({ tank, selectedIds, components, faceView, onSetPhase, onBumpCoats, onClear }) {
  const selected = components.filter((c) => selectedIds.has(c.obj.id) && c.kind !== 'portina');
  const cycles = tank.cycles || [];
  if (selected.length === 0) return null;
  return (
    <div className="flex-shrink-0 border-b border-cyan-200 bg-cyan-50 p-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-cyan-800">{selected.length} elementi selezionati — {FACE_LABEL[faceView]}</span>
        <button onClick={onClear} className="text-[11px] text-cyan-700 hover:underline">Deseleziona</button>
      </div>
      <div className="flex flex-wrap gap-1">
        {cycles.map((c) => (
          <div key={c.id} className="flex flex-wrap gap-1">
            {c.phases.map((p, idx) => (
              <button key={p.id} onClick={() => onSetPhase(c.id, idx)}
                title={c.name + ' — ' + p.name}
                className="text-[10px] font-medium rounded px-1.5 py-1 border flex items-center gap-1 bg-white hover:bg-slate-50"
                style={{ borderColor: p.color, color: p.color }}>
                <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: p.color }} />{p.name}
              </button>
            ))}
          </div>
        ))}
        <button onClick={() => onSetPhase(null, 0)} className="text-[10px] font-medium rounded px-1.5 py-1 border border-slate-300 text-slate-500 bg-white">Non assegnato</button>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-cyan-700">Mani (fase corrente):</span>
        <button onClick={() => onBumpCoats(-1)} className="w-6 h-6 rounded border border-cyan-300 text-cyan-700 text-xs bg-white">−</button>
        <button onClick={() => onBumpCoats(1)} className="w-6 h-6 rounded border border-cyan-300 text-cyan-700 text-xs bg-white">+</button>
      </div>
    </div>
  );
}

/* ============================= lista componenti ============================= */
function ComponentList({ tank, faceView, activeId, selectedIds, onPick, onToggle, onHatchStage }) {
  const shells = { kind: 'virola', items: (tank.shells || []).map((s) => ({ kind: 'virola', obj: s })) };
  const fondo = (tank.bottom && tank.bottom.plates) || [];
  const trinc = (tank.trincarino && tank.trincarino.plates) || [];
  const tettoItems = tank.roof
    ? (tank.roof.kind === 'galleggiante'
      ? [...(tank.roof.plates || []).map((p) => ({ kind: 'tetto_lamiera', obj: p })), { kind: 'pontone', obj: tank.roof.pontoon }]
      : [{ kind: 'tetto', obj: tank.roof }])
    : [];

  const rowGroups = groupBy(fondo, (p) => p.row);

  return (
    <div className="space-y-4">
      {shells.items.length > 0 && (
        <ListGroup label={`Virole (${shells.items.length})`} items={shells.items} tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={onPick} onToggle={onToggle} />
      )}

      {fondo.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Fondo — {fondo.length} lamiere</p>
          <div className="space-y-1">
            {Object.keys(rowGroups).sort((a, b) => a - b).map((row) => (
              <div key={row} className="flex items-center gap-1 flex-wrap">
                <span className="text-[9px] text-slate-400 w-6 flex-shrink-0">C{row}</span>
                {rowGroups[row].map((p) => (
                  <PlateCell key={p.id} tank={tank} faceView={faceView} kind="fondo_lamiera" obj={p} active={activeId === p.id} selected={selectedIds.has(p.id)} onToggle={onToggle} />
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {trinc.length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Trincarino — {trinc.length} lamiere</p>
          <div className="flex flex-wrap gap-1">
            {trinc.map((p) => <PlateCell key={p.id} tank={tank} faceView={faceView} kind="trincarino_lamiera" obj={p} active={activeId === p.id} selected={selectedIds.has(p.id)} onToggle={onToggle} />)}
          </div>
        </div>
      )}

      {tettoItems.length > 0 && (
        tank.roof.kind === 'galleggiante' ? (
          <div>
            <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Tetto galleggiante — {(tank.roof.plates || []).length} lamiere + pontone</p>
            <div className="flex flex-wrap gap-1 mb-1.5">
              {(tank.roof.plates || []).map((p) => <PlateCell key={p.id} tank={tank} faceView={faceView} kind="tetto_lamiera" obj={p} active={activeId === p.id} selected={selectedIds.has(p.id)} onToggle={onToggle} />)}
            </div>
            <ListGroup items={[{ kind: 'pontone', obj: tank.roof.pontoon }]} tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={onPick} onToggle={onToggle} bare />
          </div>
        ) : (
          <ListGroup label="Tetto" items={tettoItems} tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={onPick} onToggle={onToggle} />
        )
      )}

      {(tank.nozzles || []).length > 0 && (
        <ListGroup label={`Bocchelli (${tank.nozzles.length})`} items={tank.nozzles.map((n) => ({ kind: 'bocchello', obj: n }))} tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={onPick} onToggle={onToggle} />
      )}
      {(tank.stiffeners || []).length > 0 && (
        <ListGroup label={`Cordoli (${tank.stiffeners.length})`} items={tank.stiffeners.map((c) => ({ kind: 'cordolo', obj: c }))} tank={tank} faceView={faceView} activeId={activeId} selectedIds={selectedIds} onPick={onPick} onToggle={onToggle} />
      )}
      {(tank.hatches || []).length > 0 && (
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">Portine di accesso ({tank.hatches.length})</p>
          <div className="space-y-1">
            {tank.hatches.map((h) => (
              <div key={h.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer border ${h.id === activeId ? 'bg-blue-50 border-blue-300' : 'border-transparent hover:bg-slate-50'}`} onClick={() => onPick(h.id)}>
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: HATCH_STAGE_HEX[h.stage] }} />
                <span className="flex-1 min-w-0 text-xs truncate">{displayName('portina', h)}</span>
                <div className="flex gap-1 flex-shrink-0">
                  {HATCH_STAGE_ORDER.map((s) => (
                    <button key={s} onClick={(e) => { e.stopPropagation(); onHatchStage(h.id, s); }} title={HATCH_STAGE_LABEL[s]}
                      className={`w-3.5 h-3.5 rounded-sm border ${h.stage === s ? 'ring-2 ring-offset-1' : 'opacity-30 border-slate-300'}`}
                      style={{ background: HATCH_STAGE_HEX[s] }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
function groupBy(arr, fn) {
  const out = {};
  arr.forEach((x) => { const k = fn(x); (out[k] = out[k] || []).push(x); });
  return out;
}
function ListGroup({ label, items, tank, faceView, activeId, selectedIds, onPick, onToggle, bare }) {
  const body = (
    <div className="space-y-1">
      {items.map((it) => {
        const color = componentColor(tank, it.kind, it.obj, faceView);
        return (
          <div key={it.obj.id}
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer border ${it.obj.id === activeId ? 'bg-blue-50 border-blue-300' : 'border-transparent hover:bg-slate-50'}`}
            onClick={() => onPick(it.obj.id)} data-testid={`comp-row-${it.obj.id}`}>
            <input type="checkbox" className="w-3 h-3 flex-shrink-0" checked={selectedIds.has(it.obj.id)} onClick={(e) => e.stopPropagation()} onChange={() => onToggle(it.obj.id)} />
            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: color }} />
            <span className="flex-1 min-w-0 text-xs truncate">{displayName(it.kind, it.obj)}</span>
          </div>
        );
      })}
    </div>
  );
  if (bare) return body;
  return (
    <div>
      {label && <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold mb-1.5">{label}</p>}
      {body}
    </div>
  );
}
function PlateCell({ tank, faceView, kind, obj, active, selected, onToggle }) {
  const color = componentColor(tank, kind, obj, faceView);
  return (
    <button onClick={() => onToggle(obj.id)} title={displayName(kind, obj)}
      className={`w-4 h-4 rounded-sm border flex-shrink-0 ${active ? 'ring-2 ring-offset-1 ring-blue-500' : selected ? 'ring-2 ring-offset-1 ring-cyan-400' : 'border-slate-300'}`}
      style={{ background: color }} />
  );
}

/* ============================= editor dati ============================= */
function EditorForm({ draft, setDraft, onApply, onLoadExample }) {
  if (!draft) return null;
  const set = (patch) => setDraft((prev) => ({ ...prev, ...patch }));
  const setShell = (idx, field, val) => setDraft((prev) => ({ ...prev, shells: prev.shells.map((s, i) => i === idx ? { ...s, [field]: val } : s) }));
  const setNozzle = (idx, field, val) => setDraft((prev) => ({ ...prev, nozzles: prev.nozzles.map((n, i) => i === idx ? { ...n, [field]: val } : n) }));
  const setStiff = (idx, field, val) => setDraft((prev) => ({ ...prev, stiffeners: prev.stiffeners.map((c, i) => i === idx ? { ...c, [field]: val } : c) }));
  const setHatch = (idx, field, val) => setDraft((prev) => ({ ...prev, hatches: prev.hatches.map((h, i) => i === idx ? { ...h, [field]: val } : h) }));

  const firstCycleId = draft.cycles[0] ? draft.cycles[0].id : null;

  const addShell = () => {
    const last = draft.shells[draft.shells.length - 1];
    setDraft((prev) => ({ ...prev, shells: [...prev.shells, { id: nextId('V', prev.shells), diameter: last ? last.diameter : 8, height: last ? last.height : 2.4, thickness: last ? last.thickness : 8, faces: defaultFaces(firstCycleId) }] }));
  };
  const addNozzle = () => setDraft((prev) => ({ ...prev, nozzles: [...prev.nozzles, { id: nextId('B', prev.nozzles), shellId: prev.shells[0] ? prev.shells[0].id : '', label: 'Nuovo bocchello', elevation: 1, angle: 0, diameter: 100, length: 200, faces: defaultFaces(firstCycleId) }] }));
  const addStiff = () => setDraft((prev) => ({ ...prev, stiffeners: [...prev.stiffeners, { id: nextId('C', prev.stiffeners), shellId: prev.shells[0] ? prev.shells[0].id : '', label: 'Nuovo cordolo', elevation: 1, faces: defaultFaces(firstCycleId) }] }));
  const addHatch = () => setDraft((prev) => ({ ...prev, hatches: [...(prev.hatches || []), { id: nextId('P', prev.hatches || []), shellId: prev.shells[0] ? prev.shells[0].id : '', label: 'Nuova portina', elevation: 1, angle: 0, width: 600, height: 800, stage: 'da_sabbiare' }] }));

  const delShell = (idx) => setDraft((prev) => ({ ...prev, shells: prev.shells.filter((_, i) => i !== idx) }));
  const delNozzle = (idx) => setDraft((prev) => ({ ...prev, nozzles: prev.nozzles.filter((_, i) => i !== idx) }));
  const delStiff = (idx) => setDraft((prev) => ({ ...prev, stiffeners: prev.stiffeners.filter((_, i) => i !== idx) }));
  const delHatch = (idx) => setDraft((prev) => ({ ...prev, hatches: prev.hatches.filter((_, i) => i !== idx) }));

  const shellOptions = draft.shells.map((s) => <SelectItem key={s.id} value={s.id}>Virola {s.id.replace('V', '')}</SelectItem>);
  const refDiameter = draft.shells[0] ? draft.shells[0].diameter : 10;

  return (
    <div className="space-y-5 pb-2">
      <CyclesEditor draft={draft} setDraft={setDraft} />

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-700">Virole</p>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addShell}><Plus size={12} className="mr-0.5" /> Aggiungi</Button>
        </div>
        <div className="space-y-2">
          {draft.shells.map((s, i) => (
            <div key={s.id} className="border border-slate-200 rounded-md p-2 bg-slate-50">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{s.id}</span>
                <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => delShell(i)}><Trash2 size={12} /></button>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <MiniField label="Diametro (m)" type="number" step="0.01" value={s.diameter} onChange={(v) => setShell(i, 'diameter', v)} />
                <MiniField label="Altezza (m)" type="number" step="0.01" value={s.height} onChange={(v) => setShell(i, 'height', v)} />
                <MiniField label="Spessore (mm)" type="number" step="0.1" value={s.thickness} onChange={(v) => setShell(i, 'thickness', v)} />
              </div>
            </div>
          ))}
          {draft.shells.length === 0 && <p className="text-[11px] text-slate-400 text-center py-2">Nessuna virola</p>}
        </div>
      </section>

      <section>
        <p className="text-xs font-bold text-slate-700 mb-2">Fondo</p>
        <ToggleRow value={draft.bottom.type} options={[['flat', 'Piano'], ['dished', 'Bombato']]} onChange={(v) => set({ bottom: { ...draft.bottom, type: v } })} />
        <PlateGenerator label="Genera lamiere fondo" defaultDiameter={refDiameter} extraField={{ label: 'Larghezza corso (m)', key: 'plateWidth', def: 2 }}
          extraField2={{ label: 'Lunghezza lamiera (m)', key: 'plateLength', def: 8 }}
          cycles={draft.cycles} count={draft.bottom.plates.length}
          onGenerate={(diam, cid, extra) => set({ bottom: { ...draft.bottom, plates: generateBottomPlates(diam, extra.plateWidth, extra.plateLength, cid) } })} />
      </section>

      <section>
        <p className="text-xs font-bold text-slate-700 mb-2">Trincarino</p>
        <PlateGenerator label="Genera lamiere trincarino" defaultDiameter={refDiameter} extraField={{ label: 'N. lamiere', key: 'count', def: 38 }}
          cycles={draft.cycles} count={draft.trincarino.plates.length}
          onGenerate={(diam, cid, extra) => set({ trincarino: { plates: generateTrincarinoPlates(extra.count, cid) } })} />
      </section>

      <section>
        <p className="text-xs font-bold text-slate-700 mb-2">Tetto</p>
        <ToggleRow value={draft.roof.kind} options={[['fisso', 'Fisso'], ['galleggiante', 'Galleggiante']]} onChange={(v) => set({ roof: { ...draft.roof, kind: v } })} />
        {draft.roof.kind === 'fisso' ? (
          <div className="mt-2">
            <ToggleRow value={draft.roof.type} options={[['cone', 'Conico'], ['dome', 'Bombato'], ['flat', 'Piano']]} onChange={(v) => set({ roof: { ...draft.roof, type: v } })} />
          </div>
        ) : (
          <PlateGenerator label="Genera lamiere tetto (spicchi + pontone)" defaultDiameter={refDiameter} extraField={{ label: 'N. spicchi', key: 'gores', def: 24 }}
            cycles={draft.cycles} count={draft.roof.plates.length}
            onGenerate={(diam, cid, extra) => set({ roof: { ...draft.roof, plates: generateRoofPlates(extra.gores, cid) } })} />
        )}
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-700">Bocchelli</p>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addNozzle}><Plus size={12} className="mr-0.5" /> Aggiungi</Button>
        </div>
        <div className="space-y-2">
          {draft.nozzles.map((n, i) => (
            <div key={n.id} className="border border-slate-200 rounded-md p-2 bg-slate-50">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{n.id}</span>
                <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => delNozzle(i)}><Trash2 size={12} /></button>
              </div>
              <Input value={n.label} onChange={(e) => setNozzle(i, 'label', e.target.value)} className="h-6 text-[11px] mb-1.5" placeholder="Etichetta" />
              <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                <div>
                  <Label className="text-[9px] text-slate-400">Virola</Label>
                  <Select value={n.shellId} onValueChange={(v) => setNozzle(i, 'shellId', v)}>
                    <SelectTrigger className="h-6 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{shellOptions}</SelectContent>
                  </Select>
                </div>
                <MiniField label="Quota (m)" type="number" step="0.01" value={n.elevation} onChange={(v) => setNozzle(i, 'elevation', v)} />
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <MiniField label="Angolo (°)" type="number" value={n.angle} onChange={(v) => setNozzle(i, 'angle', v)} />
                <MiniField label="DN (mm)" type="number" value={n.diameter} onChange={(v) => setNozzle(i, 'diameter', v)} />
                <MiniField label="Lungh. (mm)" type="number" value={n.length} onChange={(v) => setNozzle(i, 'length', v)} />
              </div>
            </div>
          ))}
          {draft.nozzles.length === 0 && <p className="text-[11px] text-slate-400 text-center py-2">Nessun bocchello</p>}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-700">Cordoli</p>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addStiff}><Plus size={12} className="mr-0.5" /> Aggiungi</Button>
        </div>
        <div className="space-y-2">
          {draft.stiffeners.map((c, i) => (
            <div key={c.id} className="border border-slate-200 rounded-md p-2 bg-slate-50">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{c.id}</span>
                <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => delStiff(i)}><Trash2 size={12} /></button>
              </div>
              <Input value={c.label} onChange={(e) => setStiff(i, 'label', e.target.value)} className="h-6 text-[11px] mb-1.5" placeholder="Etichetta" />
              <div className="grid grid-cols-2 gap-1.5">
                <div>
                  <Label className="text-[9px] text-slate-400">Virola</Label>
                  <Select value={c.shellId} onValueChange={(v) => setStiff(i, 'shellId', v)}>
                    <SelectTrigger className="h-6 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{shellOptions}</SelectContent>
                  </Select>
                </div>
                <MiniField label="Quota (m)" type="number" step="0.01" value={c.elevation} onChange={(v) => setStiff(i, 'elevation', v)} />
              </div>
            </div>
          ))}
          {draft.stiffeners.length === 0 && <p className="text-[11px] text-slate-400 text-center py-2">Nessun cordolo</p>}
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-bold text-slate-700">Portine di accesso</p>
          <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addHatch}><Plus size={12} className="mr-0.5" /> Aggiungi</Button>
        </div>
        <div className="space-y-2">
          {(draft.hatches || []).map((h, i) => (
            <div key={h.id} className="border border-slate-200 rounded-md p-2 bg-slate-50">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[11px] font-mono font-bold text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded">{h.id}</span>
                <button className="ml-auto text-slate-400 hover:text-red-600" onClick={() => delHatch(i)}><Trash2 size={12} /></button>
              </div>
              <Input value={h.label} onChange={(e) => setHatch(i, 'label', e.target.value)} className="h-6 text-[11px] mb-1.5" placeholder="Etichetta" />
              <div className="grid grid-cols-2 gap-1.5 mb-1.5">
                <div>
                  <Label className="text-[9px] text-slate-400">Virola</Label>
                  <Select value={h.shellId} onValueChange={(v) => setHatch(i, 'shellId', v)}>
                    <SelectTrigger className="h-6 text-[11px]"><SelectValue /></SelectTrigger>
                    <SelectContent>{shellOptions}</SelectContent>
                  </Select>
                </div>
                <MiniField label="Quota (m)" type="number" step="0.01" value={h.elevation} onChange={(v) => setHatch(i, 'elevation', v)} />
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <MiniField label="Angolo (°)" type="number" value={h.angle} onChange={(v) => setHatch(i, 'angle', v)} />
                <MiniField label="Largh. (mm)" type="number" value={h.width} onChange={(v) => setHatch(i, 'width', v)} />
                <MiniField label="Alt. (mm)" type="number" value={h.height} onChange={(v) => setHatch(i, 'height', v)} />
              </div>
            </div>
          ))}
          {(draft.hatches || []).length === 0 && <p className="text-[11px] text-slate-400 text-center py-2">Nessuna portina</p>}
        </div>
      </section>

      <div className="sticky bottom-0 bg-white pt-2 border-t border-slate-200 flex gap-2">
        <Button variant="outline" size="sm" className="text-xs" onClick={onLoadExample}>Esempio</Button>
        <Button size="sm" className="text-xs flex-1" onClick={onApply} data-testid="tank-apply-btn">Genera modello 3D</Button>
      </div>
    </div>
  );
}

/* ============================= editor cicli ============================= */
function CyclesEditor({ draft, setDraft }) {
  const cycles = draft.cycles || [];
  const updateCycle = (ci, patch) => setDraft((prev) => ({ ...prev, cycles: prev.cycles.map((c, i) => i === ci ? { ...c, ...patch } : c) }));
  const updatePhase = (ci, pi, patch) => setDraft((prev) => ({ ...prev, cycles: prev.cycles.map((c, i) => i !== ci ? c : { ...c, phases: c.phases.map((p, j) => j === pi ? { ...p, ...patch } : p) }) }));
  const addPhase = (ci) => setDraft((prev) => ({ ...prev, cycles: prev.cycles.map((c, i) => i !== ci ? c : { ...c, phases: [...c.phases, { id: nextId('f', c.phases), name: 'Nuova fase', color: PHASE_COLOR_PALETTE[c.phases.length % PHASE_COLOR_PALETTE.length], coats: 1 }] }) }));
  const delPhase = (ci, pi) => setDraft((prev) => ({ ...prev, cycles: prev.cycles.map((c, i) => i !== ci ? c : { ...c, phases: c.phases.filter((_, j) => j !== pi) }) }));
  const movePhase = (ci, pi, dir) => setDraft((prev) => ({
    ...prev, cycles: prev.cycles.map((c, i) => {
      if (i !== ci) return c;
      const arr = [...c.phases];
      const j = pi + dir;
      if (j < 0 || j >= arr.length) return c;
      [arr[pi], arr[j]] = [arr[j], arr[pi]];
      return { ...c, phases: arr };
    }),
  }));
  const addCycle = () => setDraft((prev) => ({ ...prev, cycles: [...prev.cycles, { id: nextId('C', prev.cycles), name: 'Nuovo ciclo', phases: [{ id: 'f1', name: 'Fase 1', color: PHASE_COLOR_PALETTE[0], coats: 1 }] }] }));
  const duplicateCycle = (ci) => setDraft((prev) => {
    const src = prev.cycles[ci];
    const copy = JSON.parse(JSON.stringify(src));
    copy.id = nextId('C', prev.cycles);
    copy.name = src.name + ' (copia)';
    return { ...prev, cycles: [...prev.cycles, copy] };
  });
  const delCycle = (ci) => setDraft((prev) => ({ ...prev, cycles: prev.cycles.filter((_, i) => i !== ci) }));

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-slate-700">Cicli di lavorazione</p>
        <Button size="sm" variant="outline" className="h-6 text-[11px]" onClick={addCycle}><Plus size={12} className="mr-0.5" /> Nuovo ciclo</Button>
      </div>
      <div className="space-y-2">
        {cycles.map((c, ci) => (
          <div key={c.id} className="border border-slate-200 rounded-md p-2 bg-slate-50">
            <div className="flex items-center gap-1.5 mb-2">
              <Input value={c.name} onChange={(e) => updateCycle(ci, { name: e.target.value })} className="h-6 text-[11px] font-semibold flex-1" />
              <button title="Duplica" className="text-slate-400 hover:text-blue-600" onClick={() => duplicateCycle(ci)}><Copy size={12} /></button>
              <button title="Elimina" className="text-slate-400 hover:text-red-600" onClick={() => delCycle(ci)}><Trash2 size={12} /></button>
            </div>
            <div className="space-y-1">
              {c.phases.map((p, pi) => (
                <div key={p.id} className="flex items-center gap-1 bg-white border border-slate-200 rounded px-1.5 py-1">
                  <div className="flex gap-0.5">
                    {PHASE_COLOR_PALETTE.map((hex) => (
                      <button key={hex} onClick={() => updatePhase(ci, pi, { color: hex })}
                        className={`w-3 h-3 rounded-sm flex-shrink-0 ${p.color === hex ? 'ring-2 ring-offset-1 ring-slate-400' : ''}`} style={{ background: hex }} />
                    ))}
                  </div>
                  <Input value={p.name} onChange={(e) => updatePhase(ci, pi, { name: e.target.value })} className="h-6 text-[11px] flex-1 min-w-0" />
                  <Input type="number" min="1" value={p.coats} onChange={(e) => updatePhase(ci, pi, { coats: Math.max(1, parseInt(e.target.value) || 1) })} title="Numero mani" className="h-6 text-[11px] w-12 flex-shrink-0" />
                  <button onClick={() => movePhase(ci, pi, -1)} disabled={pi === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-20"><ArrowUp size={11} /></button>
                  <button onClick={() => movePhase(ci, pi, 1)} disabled={pi === c.phases.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-20"><ArrowDown size={11} /></button>
                  <button onClick={() => delPhase(ci, pi)} className="text-slate-400 hover:text-red-600"><Trash2 size={11} /></button>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" className="h-6 text-[10px] mt-1.5" onClick={() => addPhase(ci)}><Plus size={11} className="mr-0.5" /> Fase</Button>
          </div>
        ))}
        {cycles.length === 0 && <p className="text-[11px] text-slate-400 text-center py-2">Nessun ciclo definito</p>}
      </div>
    </section>
  );
}

/* ============================= generatore lamiere (UI) ============================= */
function PlateGenerator({ label, defaultDiameter, extraField, extraField2, cycles, count, onGenerate }) {
  const [diam, setDiam] = useState(defaultDiameter || 10);
  const [ev1, setEv1] = useState(extraField ? extraField.def : 0);
  const [ev2, setEv2] = useState(extraField2 ? extraField2.def : 0);
  const [cycleId, setCycleId] = useState(cycles[0] ? cycles[0].id : '');

  const doGenerate = () => {
    if (count > 0 && !window.confirm(`Rigenerare le lamiere? L'avanzamento attuale (${count} lamiere) andra' perso.`)) return;
    const extra = {};
    if (extraField) extra[extraField.key] = ev1;
    if (extraField2) extra[extraField2.key] = ev2;
    onGenerate(diam, cycleId || null, extra);
  };

  return (
    <div className="mt-2 border border-dashed border-slate-300 rounded-md p-2 bg-white">
      <p className="text-[10px] font-semibold text-slate-600 mb-1.5">{label} {count > 0 && <span className="text-slate-400 font-normal">({count} attuali)</span>}</p>
      <div className="grid grid-cols-2 gap-1.5 mb-1.5">
        <MiniField label="Diametro (m)" type="number" step="0.1" value={diam} onChange={setDiam} />
        {extraField && <MiniField label={extraField.label} type="number" step="1" value={ev1} onChange={setEv1} />}
        {extraField2 && <MiniField label={extraField2.label} type="number" step="0.5" value={ev2} onChange={setEv2} />}
        <div>
          <Label className="text-[9px] text-slate-400">Ciclo predefinito</Label>
          <Select value={cycleId} onValueChange={setCycleId}>
            <SelectTrigger className="h-6 text-[11px]"><SelectValue /></SelectTrigger>
            <SelectContent>{cycles.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <Button size="sm" variant="outline" className="h-6 text-[11px] w-full" onClick={doGenerate}>Genera lamiere</Button>
    </div>
  );
}

function MiniField({ label, value, onChange, type, step }) {
  return (
    <div>
      <Label className="text-[9px] text-slate-400">{label}</Label>
      <Input type={type || 'text'} step={step || '1'} value={value} onChange={(e) => onChange(type === 'number' ? (parseFloat(e.target.value) || 0) : e.target.value)} className="h-6 text-[11px]" />
    </div>
  );
}

function ToggleRow({ value, options, onChange }) {
  return (
    <div className="flex gap-1.5">
      {options.map(([v, label]) => (
        <button key={v} onClick={() => onChange(v)}
          className={`flex-1 text-[11px] font-medium rounded-md py-1.5 border ${value === v ? 'bg-blue-50 border-blue-300 text-blue-700 font-bold' : 'bg-slate-50 border-slate-200 text-slate-500'}`}>
          {label}
        </button>
      ))}
    </div>
  );
}
